<template>
  <GenericCard
    :locked="locked"
    has-delete
    :on-delete="onCardDelete"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="$v.section.$invalid"
    :dirty="$v.section.$anyDirty"
    :create="create"
    title="Crime"
    :section="section"
    has-footer
    has-share
    :card-identifier="cardIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <v-row>
        <v-col cols="12">
          <v-alert
            border="left"
            colored-border
            color="#2A3B4D"
            icon="mdi-information-outline"
            elevation="2"
            class="mb-6"
          >
            An applicant may be ineligible for asylum because of criminal activity. INA § 208(b)(2)(A)(ii) (particularly serious crime bar, including aggravated felonies); INA § 208(b)(2)(A)(iii) (serious non-political crime bar).
          </v-alert>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          sm="3"
        >
          <div class="text-right">
            Describe Crime at Issue
          </div>
        </v-col>
        <v-col
          cols="12"
          sm="9"
        >
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="section.crime"
                outlined
                autocomplete="off"
                label="Crime"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col
              cols="12"
              sm="6"
            >
              <ComboDate
                :date="section.date_of_crime"
                label="Date of Crime"
                outlined
                :disabled="locked"
                :uncertain="section.date_of_crime_unknown === true"
                @update-box="updateField($event, 'date_of_crime_unknown')"
                @update-date="updateField($event, 'date_of_crime')"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <ComboDate
                :date="section.date_of_conviction"
                label="Date of Conviction"
                outlined
                :disabled="locked"
                :uncertain="section.date_of_conviction_unknown === true"
                @update-box="updateField($event, 'date_of_conviction_unknown')"
                @update-date="updateField($event, 'date_of_conviction')"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <h4>Implications <span class="font-italic">(Select all that apply)</span></h4>
              <v-checkbox
                v-model="section.aggravated_felony"
                hide-details
              >
                <div slot="label">
                  Aggravated Felony
                </div>
              </v-checkbox>
              <v-checkbox
                v-model="section.particularly_serious_crime"
                hide-details
              >
                <div slot="label">
                  Particularly Serious Crime
                </div>
              </v-checkbox>
              <v-checkbox
                v-model="section.serious_non_political"
                hide-details
              >
                <div slot="label">
                  Serious non-political
                </div>
              </v-checkbox>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-textarea
            v-model="section.narrative"
            auto-grow
            outlined
            autocomplete="off"
            label="Narrative"
          />
        </v-col>
      </v-row>
    </v-form>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { useVuelidate } from '@vuelidate/core'

import { PARTY_NAMESPACE } from '@/store/party'
import { alertHandler } from '@/alertHandler'

import ComboDate from '@/components/Fields/ComboDate'
import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard'

export default {
  name: 'CrimesCard',
  components: { ComboDate, GenericCard },

  props: {
    item: {
      type: Object,
      required: true,
    },
  },

  setup () {
    return {
      $v: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      section: {},
    }
  },

  validations () {
    return {
      section: {},
    }
  },

  computed: {
    cardIdentifier () { return `crimes-card-${this.sectionIdentifier}` },
    create () { return this.item.id === null },

    locked: {
      get () {
        return this.peCrimesCardLock(this.sectionIdentifier)
      },

      set (value) {
        this.setPeCrimesCardLock({ index: this.sectionIdentifier, locked: value })
      },
    },

    sectionIdentifier () { return this.section.id || 'new' },

    subtitle () {
      if (this.create) return ''
      return `(${this.section.name})`
    },

    ...mapGetters(PARTY_NAMESPACE, [
      'peCrimesCardLock',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },
  },

  created () {
    if (!this.create) this.initPeCrimesCard(this.sectionIdentifier)
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardCreate () {
      return this.peCrimesCardCreate({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardDelete () {
      if (this.create) return this.peCrimesCardCreateUndo()
      else {
        return this.peCrimesCardDelete({ id: this.section.id, index: this.sectionIdentifier })
      }
    },

    async onCardRefresh () {
      return this.peCrimesCardRefresh({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardUpdate () {
      return this.peCrimesCardUpdate({ id: this.section.id, index: this.sectionIdentifier, section: this.section })
    },

    async onSectionUnlock () {
      return this.setPeCrimesCardLock({ index: this.sectionIdentifier, locked: false })
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else if (this.create) return this.onCardCreate()
      else return this.onCardUpdate()
    },

    updateField (value, key) { this.section[key] = value },

    ...mapMutations(PARTY_NAMESPACE, [
      'peCrimesCardCreateUndo',
      'setPeCrimesCardLock',

    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPeCrimesCard',
      'peCrimesCardCreate',
      'peCrimesCardDelete',
      'peCrimesCardRefresh',
      'peCrimesCardUpdate',
    ]),
  },
}
</script>
